import { useCallback } from 'react';

import { Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';

export const TOUR_CANCEL_COPY = {
  action: 'Cancel tour',
  link: 'Cancel',
  title: 'Cancel this tour?',
  body: 'Your tour will be removed from your schedule.',
  confirm: 'Cancel Tour',
  retry: 'Try Again',
  dismiss: 'Keep Tour',
  errorTitle: 'Unable to Cancel',
  errorBody: 'Something went wrong. Please try again.',
  canceledBadge: 'Canceled',
  canceledSchedulePrefix: 'Canceled',
} as const;

interface TourCancelDialogProps {
  open: boolean;
  canceling: boolean;
  hasError: boolean;
  onKeep: () => void;
  onConfirm: () => void;
}

export function TourCancelDialog({
  open,
  canceling,
  hasError,
  onKeep,
  onConfirm,
}: TourCancelDialogProps) {
  const handleOpenChange = useCallback(
    function handleOpenChange(nextOpen: boolean) {
      if (!nextOpen && !canceling) {
        onKeep();
      }
    },
    [canceling, onKeep],
  );
  const confirmLabel = hasError ? TOUR_CANCEL_COPY.retry : TOUR_CANCEL_COPY.confirm;

  return (
    <Dialog.Root role="alertdialog" open={open} onOpenChange={handleOpenChange}>
      <Dialog size="sm" className="p-6 sm:w-110 sm:rounded-3xl sm:p-8">
        <Dialog.Title className="pr-10 pb-3 text-subheader-sm sm:text-subheader-sm">
          {TOUR_CANCEL_COPY.title}
        </Dialog.Title>
        <Dialog.Description className="text-body-md text-od-content-secondary">
          {TOUR_CANCEL_COPY.body}
        </Dialog.Description>
        {hasError ? (
          <Text role="alert" className="pt-3 text-body-sm text-od-content-error">
            {`${TOUR_CANCEL_COPY.errorTitle}. ${TOUR_CANCEL_COPY.errorBody}`}
          </Text>
        ) : null}
        <Row className="flex-wrap justify-end gap-2 pt-5">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onKeep}
            disabled={canceling}
            aria-label={TOUR_CANCEL_COPY.dismiss}
            analyticsName="hub-tour-cancel-keep"
          >
            {TOUR_CANCEL_COPY.dismiss}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="md"
            onClick={onConfirm}
            disabled={canceling}
            loading={canceling}
            aria-label={confirmLabel}
            analyticsName="hub-tour-cancel-confirm"
          >
            {confirmLabel}
          </Button>
        </Row>
      </Dialog>
    </Dialog.Root>
  );
}
